import React from 'react';
import { LegalDoc, Section, P, List, AlphaList, Address } from '../components/LegalDoc';
export const TOC_ITEMS = [{
  number: '1',
  title: 'Agreement to These Terms'
}, {
  number: '2',
  title: 'Enterprise Agreements; Order of Precedence'
}, {
  number: '3',
  title: 'Eligibility'
}, {
  number: '4',
  title: 'Accounts and Security'
}, {
  number: '5',
  title: 'Privacy'
}, {
  number: '6',
  title: 'The Services; Plans; Use on Behalf of Clients'
}, {
  number: '7',
  title: 'Your Content'
}, {
  number: '8',
  title: 'AI Features and AI Output'
}, {
  number: '9',
  title: 'Publishing, Embeds, and Your Sites'
}, {
  number: '10',
  title: 'Acceptable Use'
}, {
  number: '11',
  title: 'Fees and Billing (Self-Serve Plans)'
}, {
  number: '12',
  title: "Maker's Intellectual Property"
}, {
  number: '13',
  title: 'Feedback'
}, {
  number: '14',
  title: 'Third-Party Services and Links'
}, {
  number: '15',
  title: 'Copyright Policy (DMCA)'
}, {
  number: '16',
  title: 'Suspension and Termination'
}, {
  number: '17',
  title: 'Changes to the Services'
}, {
  number: '18',
  title: 'Beta and Pilot Features'
}, {
  number: '19',
  title: 'Disclaimer of Warranties'
}, {
  number: '20',
  title: 'Limitation of Liability'
}, {
  number: '21',
  title: 'Indemnification'
}, {
  number: '22',
  title: 'Export Controls and Sanctions'
}, {
  number: '23',
  title: 'Governing Law; Venue; Time to Bring Claims'
}, {
  number: '24',
  title: 'Changes to These Terms'
}, {
  number: '25',
  title: 'General Terms'
}, {
  number: '26',
  title: 'Contact and Reporting'
}];
function Ref({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const element = document.getElementById(`section-${id}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <a href={`#section-${id}`} onClick={handleClick} data-tracking-id="link-6b7109e4ed217ad3">{children}</a>;
}
export default function TermsOfService() {
  return <LegalDoc title="Maker Terms of Service" lastRevised="August 5, 2026">
      <Section number="1" title="Agreement to These Terms">
        <P num="1.1">
          <span className="font-medium text-slate-800">Who we are; what these Terms cover.</span>{' '}<span>
          These Terms of Service (these &ldquo;Terms&rdquo;) are a binding agreement
          between you and Maker, Inc., 548 Market St PMB 35672, San Francisco, CA
          94104-5401 (&ldquo;Maker,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
          &ldquo;our&rdquo;). These Terms govern your access to and use of the maker.co,
          app.maker.co, ai.maker.co, and nav.maker.co websites and applications, the
          Maker platform &mdash; including AI Features (defined in</span>{' '}
          <Ref id="6"><span>Section 6.1</span></Ref><span>), embeds, and CDN content delivery &mdash; and
          the related products and services Maker provides (collectively, the
          &ldquo;Services&rdquo;).
        </span></P>
        <P num="1.2">
          <span className="font-medium text-slate-800">How you accept.</span><span> These Terms
          apply to all visitors and users of the Services. You agree to these Terms by
          accessing or using the Services, by creating an account, or by clicking a
          button or checking a box indicating acceptance. If you do not agree to these
          Terms, do not access or use the Services.
        </span></P>
        <P num="1.3">
          <span className="font-medium text-slate-800">Accepting for an organization.</span>{' '}<span>
          If you accept these Terms or use the Services on behalf of a company or other
          legal entity, you represent and warrant that you have authority to bind that
          entity to these Terms, and &ldquo;you&rdquo; and &ldquo;your&rdquo; refer to
          that entity.
        </span></P>
        <P num="1.4">
          <span className="font-medium text-slate-800">Privacy Policy.</span><span> The Maker
          Privacy Policy explains how Maker
          collects, uses, and discloses information in connection with the Services (see</span>{' '}
          <Ref id="5"><span>Section 5 (Privacy)</span></Ref><span>). The Privacy Policy is a notice
          describing our data practices; it is referenced in, but does not form part of,
          these Terms. Please read it.</span>{' '}
          <a href="https://www.maker.co/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2 hover:text-slate-600" data-tracking-id="link-privacy-policy-1-4">
            https://www.maker.co/privacy-policy
          </a>
        </P>
        <P num="1.5">
          <span className="font-medium text-slate-800">Enterprise customers.</span><span> If you
          access the Services under an organization&rsquo;s separate written agreement
          with Maker, </span><Ref id="2"><span>Section 2 (Enterprise Agreements; Order of Precedence)</span></Ref>{' '}<span>
          explains how that agreement and these Terms fit together.
        </span></P>
      </Section>

      <Section number="2" title="Enterprise Agreements; Order of Precedence">
        <P num="2.1">
          <span className="font-medium text-slate-800">Key terms.</span><span> An &ldquo;Order
          Form&rdquo; is an ordering document or online purchase or checkout flow between
          you (or your organization) and Maker that specifies plan, fees, term, and usage
          allotments. An &ldquo;Enterprise Agreement&rdquo; is a separate written
          agreement executed by Maker and an organization, whether or not negotiated
          &mdash; for example, a SaaS Services Agreement together with one or more Order
          Forms. An &ldquo;Authorized User&rdquo; is an individual whom an organization
          authorizes to access the Services under its account or Enterprise Agreement
          &mdash; for example, the organization&rsquo;s employees, contractors, and
          agents acting on its behalf.
        </span></P>
        <P num="2.2">
          <span className="font-medium text-slate-800">Enterprise Agreements control.</span>{' '}<span>
          If you access or use the Services under an organization&rsquo;s Enterprise
          Agreement, then to the extent of any conflict between these Terms and that
          Enterprise Agreement (including its Order Forms), the Enterprise Agreement
          controls for that organization and its Authorized Users, and these Terms apply
          only to the extent they do not conflict with it. Acceptance of these Terms by
          an Authorized User &mdash; for example, through an in-app click-through &mdash;
          does not amend, supersede, or replace the organization&rsquo;s Enterprise
          Agreement.
        </span></P>
        <P num="2.3">
          <span className="font-medium text-slate-800">If you have no Enterprise Agreement.</span>{' '}<span>
          If you access or use the Services other than under an Enterprise Agreement
          &mdash; including as a visitor to our websites or on a free or self-serve paid
          plan &mdash; these Terms govern that access and use in full.
        </span></P>
      </Section>

      <Section number="3" title="Eligibility">
        <p>
          To use the Services, you must be (a) at least 18 years old and legally capable
          of entering into a binding contract, or (b) a duly formed legal entity acting
          through individuals who are authorized to bind it. You may not use the Services
          if applicable law bars you from doing so. The Services are not intended for
          children: if you are under 13, do not use the Services.
        </p>
      </Section>

      <Section number="4" title="Accounts and Security">
        <P num="4.1">
          <span className="font-medium text-slate-800">Registration.</span><span> Some features
          of the Services require an account. You must provide accurate, current, and
          complete registration information and keep it up to date. You may not use
          another user&rsquo;s account without that user&rsquo;s permission. If your
          registration information is or becomes inaccurate, incomplete, or misleading,
          Maker may suspend or terminate your account as described in</span>{' '}
          <Ref id="16"><span>Section 16 (Suspension and Termination)</span></Ref><span>.
        </span></P>
        <P num="4.2">
          <span className="font-medium text-slate-800">Credentials; responsibility for your account.</span>{' '}<span>
          You are responsible for keeping your account credentials confidential and
          secure and for all activity that occurs under your account. Maker is not
          responsible for any loss or damage arising from unauthorized use of your
          account to the extent it results from your failure to comply with this Section
          4.
        </span></P>
        <P num="4.3">
          <span className="font-medium text-slate-800">Notify us.</span><span> Notify Maker
          promptly at support@maker.co if you suspect or become aware of any unauthorized
          use of your account or credentials or any other breach of security affecting
          the Services.
        </span></P>
        <P num="4.4">
          <span className="font-medium text-slate-800">Third-party sign-in.</span><span> If you
          create or access your account using a third-party sign-in service (for example,
          a social login or your organization&rsquo;s single sign-on provider), your use
          of that service is governed by the third party&rsquo;s own terms and privacy
          policy, and Maker is not responsible for that service. See</span>{' '}
          <Ref id="14"><span>Section 14 (Third-Party Services and Links)</span></Ref><span>.
        </span></P>
      </Section>

      <Section number="5" title="Privacy">
        <P num="5.1">
          <span className="font-medium text-slate-800">Privacy Policy.</span><span> The Maker
          Privacy Policy, available at</span>{' '}
          <a href="https://www.maker.co/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2 hover:text-slate-600" data-tracking-id="link-privacy-policy-5-1">
            https://www.maker.co/privacy-policy
          </a><span>
          , describes how Maker collects, uses, and discloses information in
          connection with the Services, including personal information. Please review
          it carefully, it explains our data practices and the choices available
          to you.
        </span></P>
        <P num="5.2">
          <span className="font-medium text-slate-800">Preservation and disclosure.</span>{' '}<span>
          You acknowledge that Maker may preserve and may disclose your account
          information and your Customer Content (defined in</span>{' '}
          <Ref id="6"><span>Section 6.1</span></Ref><span>) if required to do so by law, or if we believe in
          good faith that preservation or disclosure is reasonably necessary to: (a)
          comply with legal process or a lawful request from law enforcement or another
          government authority; (b) enforce these Terms; (c) respond to claims that
          content violates the rights of others; or (d) protect the rights, property, or
          safety of Maker, our users, or the public. Where the information involved is
          personal information, we will handle it as described in the Privacy Policy.
        </span></P>
        <P num="5.3">
          <span className="font-medium text-slate-800">Data processing for business customers.</span>{' '}<span>
          To the extent Maker processes personal information on behalf of a business
          customer in order to provide the Services (for example, the technical
          telemetry described in the Privacy Policy), Maker acts as that customer&rsquo;s
          service provider or processor, and with respect to personal information
          subject to the CCPA Maker will: process it only to provide the Services and for
          the business purposes described in these Terms or an applicable Order Form or
          data processing addendum; not sell or share it; not retain, use, or disclose it
          outside the direct business relationship between the parties or for any purpose
          other than those business purposes; and cooperate with the customer in
          responding to verified consumer-rights requests; not combine that personal
          information with personal information Maker receives from or on behalf of any
          other person, or that Maker collects from its own interaction with the
          consumer, except as applicable law permits a service provider to do; provide
          the same level of privacy protection with respect to that personal information
          as the CCPA requires of the customer; notify the customer if Maker determines
          that it can no longer meet its obligations under the CCPA; and permit the
          customer to take reasonable and appropriate steps to confirm that Maker uses
          that personal information consistently with the customer&rsquo;s obligations
          under the CCPA, including by reviewing Maker&rsquo;s then-current security and
          privacy documentation on request, and, upon notice, to stop and remediate any
          unauthorized use of that personal information. Where a data processing
          addendum is required, the parties will enter into Maker&rsquo;s then-current
          addendum, made available by Maker upon request, which upon acceptance forms
          part of these Terms for that customer. This Section 5.3 states contractual
          commitments and does not incorporate the Privacy Policy into these Terms; the
          form-submission representation in </span><Ref id="9"><span>Section 9.3</span></Ref><span> and the
          safeguards commitment in </span><Ref id="9"><span>Section 9.4</span></Ref><span> also apply.
        </span></P>
        <P num="5.4">
          <span className="font-medium text-slate-800">Data processing addendum.</span>{' '}<span>
          Maker does not publish its data processing addendum. If your organization
          requires one, email legal@maker.co and we will send our then-current
          addendum. On acceptance it forms part of these Terms for that customer. If
          your organization has an Enterprise Agreement with Maker, the data processing
          terms of that agreement apply instead, and the order-of-precedence provision
          of that agreement governs any conflict.
        </span></P>
        <P num="5.5">
          <span className="font-medium text-slate-800">Subprocessor list.</span><span> Maker
          maintains a current list of the subprocessors that process data on behalf of
          business customers in providing the Services. It is published in Section 14 of
          the Privacy Policy at</span>{' '}
          <a href="https://www.maker.co/privacy-policy#section-14" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline underline-offset-2 hover:text-slate-600" data-tracking-id="link-tos-subprocessor-list">https://www.maker.co/privacy-policy</a><span>. That list covers those subprocessors;
          the vendors that support Maker&rsquo;s own business are described in the
          Privacy Policy by category. Notice of additions to the list, and any right to
          object to a new subprocessor, are governed by the data processing addendum
          described in Section 5.4 or by your Enterprise Agreement.
        </span></P>
      </Section>

      <Section number="6" title="The Services; Plans; Use on Behalf of Clients">
        <P num="6.1">
          <span className="font-medium text-slate-800">What the Services do.</span><span> The
          Services provide AI-native tools to create, edit, moderate, host, and deliver
          web content &mdash; landing pages, page sections, images and video, and web
          apps &mdash; and to publish that content into your own websites and
          applications. Features of the Services that use artificial-intelligence models
          to generate, edit, personalize, or moderate content are &ldquo;AI
          Features.&rdquo; Content and materials that you or your Authorized Users
          upload to, import into, create in, or publish through the Services &mdash;
          including AI Output (defined in</span>{' '}
          <Ref id="8"><span>Section 8 (AI Features and AI Output)</span></Ref><span>) that you incorporate
          &mdash; are &ldquo;Customer Content.&rdquo; The websites, applications, and
          other digital properties owned or controlled by you, or by clients you are
          authorized to represent under </span><Ref id="6"><span>Section 6.4</span></Ref><span>, where you embed or
          display published Customer Content are &ldquo;Customer Sites.&rdquo; Published
          Customer Content is delivered to Customer Sites through a client-side
          JavaScript embed and served through content-delivery networks, as described in</span>{' '}
          <Ref id="9"><span>Section 9 (Publishing, Embeds, and Your Sites)</span></Ref><span>.
        </span></P>
        <P num="6.2">
          <span className="font-medium text-slate-800">Your right to use the Services.</span>{' '}<span>
          Subject to these Terms and the limits of your plan, Maker grants you a limited,
          non-exclusive, non-transferable right to access and use the Services during
          your subscription or other applicable term: (a) for your own business
          purposes; and (b) if permitted under Section 6.4, on behalf of your clients.
          This right includes deploying the Maker embed code on Customer Sites to display
          your published Customer Content. Except as Section 6.4 or a separate written
          agreement with Maker expressly permits, you may not resell, sublicense,
          white-label, or transfer the Services; provide the Services to any third party
          as a service bureau, agency offering, or managed service; or otherwise make the
          Services available to anyone else. Maker reserves all rights not expressly
          granted (see </span><Ref id="12"><span>Section 12 (Maker&rsquo;s Intellectual Property)</span></Ref><span>
          ).
        </span></P>
        <P num="6.3">
          <span className="font-medium text-slate-800">Plans, Views, and Credits.</span>{' '}<span>
          The Services are available on free and paid plans. Each plan&rsquo;s features,
          pricing, and usage allotments are described on the Maker pricing page or in an
          Order Form between you (or your organization) and Maker. Usage is metered in
          two units: &ldquo;Views,&rdquo; which are content-load events that meter
          delivery of your published Customer Content, and &ldquo;Credits,&rdquo; which
          are the usage units that meter your consumption of AI Features across the
          underlying AI models. Your plan may include limits on usage and storage,
          including Views and Credits allotments. We may enforce plan limits, including
          by restricting usage that exceeds your allotments; options for additional
          capacity are as described on the pricing page or in your Order Form. Fees and
          billing for self-serve plans are described in</span>{' '}
          <Ref id="11"><span>Section 11 (Fees and Billing (Self-Serve Plans))</span></Ref><span>.
        </span></P>
        <P num="6.4">
          <span className="font-medium text-slate-800">Use on behalf of clients.</span><span> By
          default, the Services are for your own business: you may use them only for
          Customer Sites that you own and operate. Use of the Services on behalf of
          clients &mdash; for example, as their agency, consultancy, or service provider
          &mdash; is permitted on plans that include client or agency use, as described
          on the Maker pricing page or in your Order Form, or where a separate written
          agreement between you (or your organization) and Maker permits it, in which
          case that agreement controls. If you use the Services on behalf of a client:
        </span></P>
        <AlphaList items={['you represent and warrant that you are authorized to act for that client, including to create and publish content for that client and to embed published Customer Content on that client’s sites;', 'content you upload, create, or publish for a client is Customer Content under these Terms, and you are responsible for it to the same extent as your own;', 'the client sites where you embed published Customer Content are Customer Sites;', <>
              you will ensure that your use of the Services for each client, and that
              client&rsquo;s content and Customer Sites, comply with these Terms,
              including <Ref id="10">Section 10 (Acceptable Use)</Ref>, and you remain
              responsible for all activity under your account; and
            </>, 'these Terms do not create any contractual relationship between Maker and your clients, and a client has rights against Maker only under that client’s own agreement with Maker, if any.']} />
      </Section>

      <Section number="7" title="Your Content">
        <P num="7.1">
          <span className="font-medium text-slate-800">You own your Customer Content.</span>{' '}<span>
          As between you and Maker, you retain all ownership and intellectual-property
          rights in your Customer Content. Nothing in these Terms transfers ownership of
          Customer Content to Maker.
        </span></P>
        <P num="7.2">
          <span className="font-medium text-slate-800">The license you grant Maker.</span>{' '}<span>
          So that we can operate the product, you grant Maker a limited, non-exclusive,
          worldwide, royalty-free license to host, store, cache, reproduce, adapt as
          needed for technical delivery (for example, resizing, reformatting, or
          transcoding), modify and create derivative works of your Customer Content as
          you direct through features of the Services (for example, AI editing and
          personalization), display, perform, transmit, and serve your Customer Content
          &mdash; including through Maker&rsquo;s hosting, infrastructure, and
          content-delivery providers &mdash; solely as necessary to operate, provide,
          secure, and support the Services, including delivering your published Customer
          Content to Customer Sites. This license exists to run the Services and for no
          other purpose. It does not permit Maker to sell Customer Content or to use it
          for purposes unrelated to providing the Services, and it ends when the Customer
          Content is deleted from the Services, subject to</span>{' '}
          <Ref id="16"><span>Section 16 (Suspension and Termination)</span></Ref><span> and the Privacy
          Policy.
        </span></P>
        <P num="7.3">
          <span className="font-medium text-slate-800">Public and private content.</span>{' '}<span>
          Some features let you make Customer Content publicly available &mdash; for
          example, pages you publish to the open web. Customer Content that you elect to
          make publicly available is &ldquo;Public Content.&rdquo; Customer Content that
          you have not made public is private to your account and is handled as
          described in these Terms and the Privacy Policy. These Terms themselves do not
          include confidentiality obligations; if your organization requires contractual
          confidentiality commitments, they are available through an Enterprise
          Agreement (see</span>{' '}
          <Ref id="2"><span>Section 2 (Enterprise Agreements; Order of Precedence)</span></Ref><span>).
          Making Customer Content public does not, by itself, grant other users any
          rights in it beyond viewing it as published; any broader sharing or reuse
          happens only through the publishing and permission choices you make. Public
          Content made available by other users is the responsibility of the users who
          publish it; Maker does not endorse it.
        </span></P>
        <P num="7.4">
          <span className="font-medium text-slate-800">Promotional use.</span><span> Maker will
          not use your Customer Content to advertise or promote Maker or the Services
          except: (a) with your prior consent; or (b) in the case of Public Content,
          which we may display, feature, or link to in order to show what can be built
          with the Services. You may withdraw your consent, make Public Content private
          again, or tell us &mdash; through available account settings or by contacting
          support@maker.co &mdash; that you do not want your Public Content featured, in
          each case at any time, and we will stop making new promotional uses of that
          content within a reasonable time after you do; you do not need to unpublish
          content to opt out. Maker will not rely on clause (b) to feature the Public
          Content of an organization with an Enterprise Agreement or Order Form without
          that organization&rsquo;s consent. Withdrawal or opt-out operates prospectively:
          it does not require Maker to recall promotional materials already published or
          distributed.
        </span></P>
        <P num="7.5">
          <span className="font-medium text-slate-800">Your responsibilities for your content.</span>{' '}<span>
          You are solely responsible for your Customer Content and the consequences of
          publishing it. You represent and warrant that: (a) you have, and will
          maintain, all rights, licenses, consents, and permissions needed to submit your
          Customer Content and to grant the license in Section 7.2; and (b) your Customer
          Content, and your use and publication of it, comply with these Terms
          (including </span><Ref id="10"><span>Section 10 (Acceptable Use)</span></Ref><span>) and applicable law
          and do not infringe or misappropriate the rights of any person. Customer
          Content is the responsibility of the person or organization that provides it.
          Maker does not endorse Customer Content and makes no guarantee about its
          reliability or accuracy (see</span>{' '}
          <Ref id="19"><span>Section 19 (Disclaimer of Warranties)</span></Ref><span>).
        </span></P>
      </Section>

      <Section number="8" title="AI Features and AI Output">
        <P num="8.1">
          <span className="font-medium text-slate-800">AI Features and Credits.</span><span> The
          Services include AI Features, such as tools that generate, edit, personalize,
          or moderate content in response to your inputs and instructions. Content
          generated by AI Features in response to your inputs or instructions is
          &ldquo;AI Output.&rdquo; Your use of AI Features draws down Credits (see</span>{' '}
          <Ref id="6"><span>Section 6.3</span></Ref><span>).
        </span></P>
        <P num="8.2">
          <span className="font-medium text-slate-800">Your inputs.</span><span> You are
          responsible for the inputs, prompts, and materials you provide to AI Features,
          including for having all rights needed to use them. Inputs you upload to or
          import into the Services are Customer Content and are treated as described in</span>{' '}
          <Ref id="7"><span>Section 7 (Your Content)</span></Ref><span>.
        </span></P>
        <P num="8.3">
          <span className="font-medium text-slate-800">You own your AI Output.</span><span> As
          between you and Maker, you own the AI Output generated for you, and Maker
          assigns to you any right, title, and interest it may have in that AI Output. AI
          Output that you incorporate into your pages or projects becomes part of your
          Customer Content.
        </span></P>
        <P num="8.4">
          <span className="font-medium text-slate-800">No guarantee of uniqueness.</span>{' '}<span>
          AI models generate content based on patterns in their training data, and
          similar inputs can produce similar outputs. Maker does not guarantee that AI
          Output is unique, original, or protectable by copyright or other
          intellectual-property rights, and your ownership of your AI Output does not
          extend to the same or similar output independently generated for other
          customers.
        </span></P>
        <P num="8.5">
          <span className="font-medium text-slate-800">Review before you use or publish.</span>{' '}<span>
          AI Output can be inaccurate, incomplete, or unsuitable for your purposes. You
          must review AI Output before you use or publish it. You are responsible for
          confirming that AI Output is accurate and appropriate for your use and that
          your use of it complies with applicable laws and regulations, including the
          advertising, marketing, and industry-specific rules that apply to you and,
          where relevant, to your clients (see </span><Ref id="6"><span>Section 6.4</span></Ref><span>). AI Output
          you publish is your Customer Content, and Section 7.5 applies to it. AI
          Features and AI Output are provided without warranties of any kind, as
          described in </span><Ref id="19"><span>Section 19 (Disclaimer of Warranties)</span></Ref><span>.
        </span></P>
        <P num="8.6">
          <span className="font-medium text-slate-800">Model training.</span><span> Maker does
          not use your Customer Content, your inputs and prompts, AI Output generated
          for you, or telemetry that identifies you or your Authorized Users, to train,
          fine-tune, or otherwise improve any artificial-intelligence or
          machine-learning model, or any generally available product, without your
          written consent. Maker does not sell or license that material to anyone else
          for that purpose, and Maker does not authorize human review or annotation of
          it for that purpose.
        </span></P>
        <p>
          When Maker engages a third-party provider to store or process that material
          in delivering the Services, Maker requires either a contractual prohibition
          on using it to train or improve the provider&rsquo;s own models, or, where the
          provider offers a setting that disables such training, Maker enables that
          setting. Maker does not knowingly engage a provider that offers neither. On
          request, Maker will identify the restriction or setting that applies to a
          given provider. If Maker learns that a provider has used that material to
          train its models, Maker will notify you and will either remediate the matter
          or replace the provider.
        </p>
        <p>
          Nothing in this Section restricts Maker personnel from accessing data as
          necessary to provide support, investigate abuse, or maintain the security of
          the Services.
        </p>
      </Section>

      <Section number="9" title="Publishing, Embeds, and Your Sites">
        <P num="9.1">
          <span className="font-medium text-slate-800">How publishing works.</span><span> When
          you publish Customer Content, the Services make it available for delivery to
          your Customer Sites, typically through a JavaScript embed you install on your
          pages, with content served through content-delivery networks. You control what
          you publish, where you embed it, and when you take it down.
        </span></P>
        <P num="9.2">
          <span className="font-medium text-slate-800">Your sites are your responsibility.</span>{' '}<span>
          Customer Sites belong to you or to the clients you represent, not to Maker. You
          are responsible for Customer Sites, including: (a) their content, operation,
          and legal compliance; (b) providing any terms, privacy notices, and disclosures
          your visitors are entitled to receive; and (c) obtaining any consents required
          by applicable law (for example, cookie or tracking consents) for the
          analytics, metering, personalization, and similar technologies that operate on
          your pages in connection with the Services or with any third-party tools you
          use.
        </span></P>
        <P num="9.3">
          <span className="font-medium text-slate-800">Forms.</span><span> When visitors submit
          an embedded Maker form on your pages, the submission is transmitted directly to
          the destination or destinations you configure, and Maker does not receive or
          store the content of form submissions. You are responsible for the
          destinations you configure, for your handling of form submissions, and for
          giving your visitors any legally required notices about the data you collect.
          Do not configure form destinations that transmit submissions into
          Maker&rsquo;s systems, and do not use forms to direct into Maker&rsquo;s
          systems the restricted data categories described in Section 10.1(j).
        </span></P>
        <P num="9.4">
          <span className="font-medium text-slate-800">Security.</span><span> Maker maintains
          reasonable administrative, technical, and organizational safeguards designed to
          protect the Services and the delivery of published Customer Content. No method
          of transmission, storage, or delivery is completely secure, however, and Maker
          does not guarantee security. You are responsible for the security of Customer
          Sites and of the systems you control, including the destinations you configure
          for form submissions.
        </span></P>
      </Section>

      <Section number="10" title="Acceptable Use">
        <P num="10.1">
          <span className="font-medium text-slate-800">Rules.</span><span> You must use the
          Services in compliance with applicable law and this Section 10. In connection
          with the Services, you must not, and must not permit or encourage anyone else
          to:
        </span></P>
        <AlphaList items={['upload, publish, or transmit content that is unlawful, defamatory, harassing, threatening, abusive, hateful, pornographic, or sexually exploitative, or that invades another person’s privacy;', 'infringe, misappropriate, or violate the copyright, trademark, patent, trade-secret, publicity, privacy, or other rights of any person, or publish content in breach of a confidentiality or fiduciary obligation you owe to anyone;', 'upload or distribute viruses, worms, malware, or other harmful or malicious code, or use the Services to deliver any of them to others;', 'impersonate any person or entity, misrepresent your affiliation with any person or entity, or provide false or deceptive information to Maker or to visitors to your pages;', 'create or publish pages or content designed to phish, defraud, deceive, or mislead, including pages that imitate another organization’s site or misrepresent the origin of content;', 'send spam or other unsolicited or unauthorized advertising, promotional materials, or solicitations, or use communication features of the Services for unauthorized commercial solicitation;', 'engage in unlawful surveillance or tracking of individuals, or use the Services to collect information about individuals in violation of applicable law;', 'collect, harvest, or use personal information about other users of the Services without authorization, or solicit personal information from children under 13 or from any other minor where prohibited by applicable law;', <>
              resell, sublicense, white-label, or provide the Services to any third party
              as a service bureau, agency offering, or managed service, or use the
              Services on behalf of any third party, except as expressly permitted under{' '}
              <Ref id="6">Section 6.4</Ref>;
            </>, 'direct sensitive or regulated data into Maker’s systems — including, for example, protected health information, payment-card data, government-issued identification numbers, or personal information of children under 13 — whether through forms, uploads, or otherwise; the Services are not designed or intended to receive those categories of data (forms that deliver submissions directly to systems you or your clients control, as described in Section 9.3 (Forms), do not direct data into Maker’s systems);', 'access the Services, or collect content or data from them, using robots, spiders, scrapers, or other automated means, or through bulk or high-volume automated requests, except (i) through APIs, features, or interfaces that Maker provides or approves for automated access, used within your plan limits, and (ii) that operators of public search engines may use spiders to index materials from the Services solely to create publicly available searchable indices (and not caches or archives), a permission Maker may revoke generally or in specific cases at any time;', 'bypass, manipulate, or artificially generate or suppress Views, Credits, or any other usage metering, or otherwise circumvent plan limits or billing;', 'circumvent, disable, probe, or interfere with security-related features of the Services or features that prevent or restrict use or copying of content or that enforce plan limits; attempt to gain unauthorized access to any account, system, or network; or interfere with or disrupt the integrity or performance of the Services;', 'decompile, disassemble, reverse engineer, or otherwise attempt to derive the source code or underlying ideas of the Services, except to the extent applicable law prohibits that restriction; or', 'use the Services to violate any applicable law, statute, ordinance, or regulation, or encourage or assist anyone else to do so.']} />
        <P num="10.2">
          <span className="font-medium text-slate-800">Enforcement.</span><span> We may
          investigate suspected violations of these Terms. We may remove or disable
          access to Customer Content that we reasonably believe violates this Section 10
          or applicable law, or where removal or disabling is reasonably necessary to
          comply with a legal obligation or a governmental or court order, or to protect
          the Services, Maker, our users, or third parties; and we may suspend or
          terminate access to the Services, in each case as described in</span>{' '}
          <Ref id="16"><span>Section 16 (Suspension and Termination)</span></Ref><span>. A removal or
          disabling done in good faith under this Section is not a breach of these
          Terms. To report a violation, contact us as described in</span>{' '}
          <Ref id="26"><span>Section 26 (Contact and Reporting)</span></Ref><span>.
        </span></P>
      </Section>

      <Section number="11" title="Fees and Billing (Self-Serve Plans)">
        <P num="11.1">
          <span className="font-medium text-slate-800">Fees.</span><span> If you purchase a
          paid self-serve plan, you agree to pay the fees for that plan as stated on our
          pricing page or in your Order Form at the time of purchase. You authorize
          Maker to charge your designated payment method for all fees due, including
          renewal fees, until you cancel.
        </span></P>
        <P num="11.2">
          <span className="font-medium text-slate-800">Renewal and cancellation.</span>{' '}<span>{" Paid self-serve subscriptions renew automatically for successive periods of the same length, at the then-current price, until you cancel, and the recurring charge is billed to your payment method each period. Before you purchase, we present the auto-renewal terms, price, and billing frequency clearly and obtain your affirmative agreement to them; where the law requires, we also send a purchase acknowledgment with cancellation instructions and advance renewal reminders. You may cancel at any time through a self-service cancellation option in your account, or by contacting support@maker.co. Cancel before the next renewal date to avoid the next charge. Cancellation takes effect at the end of the then-current billing period. If we change the pricing of your self-serve plan, the change will take effect no earlier than your next renewal, and we will give you notice before it takes effect."}</span></P>
        <P num="11.3">
          <span className="font-medium text-slate-800">Usage allotments.</span><span> Your plan
          includes allotments of Views and Credits, as described on the pricing page or
          in your Order Form.
        </span></P>
        <P num="11.4">
          <span className="font-medium text-slate-800">Taxes.</span><span> Our fees do not
          include taxes. You are responsible for all sales, use, value-added,
          goods-and-services, and similar taxes, levies, and duties on your purchases,
          except taxes based on Maker&rsquo;s net income. If we are required to collect
          or pay taxes on your purchase, they will be added to your charge or invoiced
          to you.
        </span></P>
        <P num="11.5">
          <span className="font-medium text-slate-800">Refunds.</span><span> Except as required
          by applicable law or as Maker expressly states at the time of purchase,
          payments are non-refundable, and we do not provide prorated refunds for
          partial billing periods or for unused Views or Credits.
        </span></P>
        <P num="11.6">
          <span className="font-medium text-slate-800">Nonpayment.</span><span> If fees you owe
          are past due, Maker may suspend or terminate your access to paid features or
          the Services as described in</span>{' '}
          <Ref id="16"><span>Section 16 (Suspension and Termination)</span></Ref><span>.
        </span></P>
        <P num="11.7">
          <span className="font-medium text-slate-800">Enterprise plans.</span><span> Pricing,
          billing, and payment terms for organizations with an Enterprise Agreement are
          set out in the applicable Enterprise Agreement and Order Form, which control
          over this Section 11 as described in</span>{' '}
          <Ref id="2"><span>Section 2 (Enterprise Agreements; Order of Precedence)</span></Ref><span>.
        </span></P>
      </Section>

      <Section number="12" title="Maker's Intellectual Property">
        <P num="12.1">
          <span className="font-medium text-slate-800">Ownership.</span><span> The Services
          &mdash; including the Maker platform, software, embed code, interfaces,
          designs, templates, text, graphics, and other content Maker provides, but
          excluding Customer Content &mdash; are owned by Maker or its licensors and are
          protected by United States and international intellectual-property laws. Maker
          and its licensors reserve all rights not expressly granted in these Terms.
        </span></P>
        <P num="12.2">
          <span className="font-medium text-slate-800">Your use rights are plan-scoped.</span>{' '}<span>
          These Terms grant you only the rights expressly stated, for the plan you are
          on; they do not transfer any Maker intellectual property to you. Those rights
          include using materials Maker provides through the features of the Services
          (for example, templates) within Customer Content that you create and publish
          through the Services &mdash; commercial publication of your own content is
          what the product is for.
        </span></P>
        <P num="12.3">
          <span className="font-medium text-slate-800">Trademarks.</span><span> &ldquo;Maker&rdquo;
          and Maker&rsquo;s other trademarks, service marks, and logos are the property
          of Maker, Inc. You may not use them without Maker&rsquo;s prior written
          permission.
        </span></P>
        <P num="12.4">
          <span className="font-medium text-slate-800">Restrictions.</span><span> Except as
          these Terms expressly permit, you may not copy, modify, distribute, sell,
          lease, publicly display, or create derivative works of any part of the
          Services or of content Maker provides, and you may not remove or obscure any
          proprietary notices.
        </span></P>
      </Section>

      <Section number="13" title="Feedback">
        <p>
          You have no obligation to give Maker suggestions, ideas, or other input about
          the Services (&ldquo;Feedback&rdquo;). If you choose to provide Feedback, you
          grant Maker a perpetual, irrevocable, worldwide, non-exclusive, royalty-free
          license to use, reproduce, modify, and incorporate it into the Services and
          Maker&rsquo;s business for any purpose, without restriction and without
          compensation to you. This is a license, not a transfer: you keep any ownership
          rights you have in your Feedback. Maker has no confidentiality obligation with
          respect to Feedback, so please do not include confidential information in it.
          Feedback is not Customer Content.
        </p>
      </Section>

      <Section number="14" title="Third-Party Services and Links">
        <P num="14.1">
          <span className="font-medium text-slate-800">Third-party sign-in and integrations.</span>{' '}<span>
          The Services may allow you to sign in using a third-party identity provider
          (for example, a social login or your organization&rsquo;s single sign-on) or to
          connect the Services to third-party products and services, such as analytics
          tools and other integrations. Your use of any third-party service is governed
          by that third party&rsquo;s own terms and privacy policy, not by these Terms,
          and you are responsible for complying with them. Maker does not control
          third-party services and is not responsible for their availability, security,
          functionality, or data practices, or for changes a third party makes that
          affect an integration.
        </span></P>
        <P num="14.2">
          <span className="font-medium text-slate-800">Links.</span><span> The Services and
          Customer Content may contain links to third-party websites and resources that
          Maker does not own or control. Links are provided for convenience only. A link
          does not mean that Maker endorses, or is responsible for, the linked site or
          its content, products, services, or practices.
        </span></P>
        <P num="14.3">
          <span className="font-medium text-slate-800">Your dealings with third parties.</span>{' '}<span>
          Any dealings or transactions between you and a third party found on or through
          the Services &mdash; including other users and integration providers &mdash;
          are solely between you and that third party. Maker does not monitor those
          dealings, is not a party to them, and is not responsible for any loss or
          dispute arising out of them. You assume the risks of your dealings with third
          parties, and to the fullest extent permitted by law neither Maker nor its
          affiliates is liable for any loss, damage, or dispute arising out of your
          dealings with any third party, your use of any third-party service, or any
          third-party promotions, campaigns, or offerings accessed through the Services.
        </span></P>
      </Section>

      <Section number="15" title="Copyright Policy (DMCA)">
        <P num="15.1">
          <span className="font-medium text-slate-800">Respect for copyright; repeat infringers.</span>{' '}<span>
          Maker respects the intellectual-property rights of others and expects you to
          do the same. Do not publish Customer Content that contains copyrighted
          material belonging to others unless you have the right to use it. Maker
          responds to notices of claimed copyright infringement in accordance with the
          Digital Millennium Copyright Act (DMCA), and it is Maker&rsquo;s policy, in
          appropriate circumstances, to terminate the accounts of users who are repeat
          infringers.
        </span></P>
        <P num="15.2">
          <span className="font-medium text-slate-800">Submitting a DMCA takedown notice.</span>{' '}<span>
          If you are a copyright owner, or authorized to act on behalf of one, and you
          believe material on the Services infringes your copyright, send our Designated
          Agent (Section 15.6) a written notice that includes:
        </span></P>
        <AlphaList items={['a physical or electronic signature of the copyright owner or of a person authorized to act on behalf of the owner of the exclusive right allegedly infringed;', 'identification of the copyrighted work or works claimed to have been infringed;', 'identification of the material claimed to be infringing, with information reasonably sufficient to permit Maker to locate it (URLs are the fastest way to help us find content);', 'information reasonably sufficient to permit Maker to contact you, such as your address, telephone number, and email address;', 'a statement that you have a good-faith belief that use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law; and', 'a statement, made under penalty of perjury, that the information in the notice is accurate and that you are the copyright owner or are authorized to act on the copyright owner’s behalf.']} />
        <p>See 17 U.S.C. § 512(c)(3) for more information. A notice that does not include all of this information may not be effective under the DMCA.</p>
        <P num="15.3">
          <span className="font-medium text-slate-800">How Maker responds.</span><span> Upon
          receipt of a valid DMCA notice, Maker will act expeditiously to remove or
          disable access to the material identified in the notice and will take
          reasonable steps to promptly notify the user who posted it. Maker may also, in
          its discretion, remove or disable access to material it believes in good faith
          may infringe the rights of others, and may terminate the accounts of users it
          believes to be repeat infringers.
        </span></P>
        <P num="15.4">
          <span className="font-medium text-slate-800">Counter-notice.</span><span> If you
          believe material you posted was removed or disabled as a result of mistake or
          misidentification &mdash; including because you have authorization to use it
          or the use is otherwise lawful &mdash; you may send our Designated Agent a
          written counter-notice that includes:
        </span></P>
        <AlphaList items={['your physical or electronic signature;', 'identification of the material that was removed or disabled and the location at which it appeared before it was removed or disabled;', 'a statement, under penalty of perjury, that you have a good-faith belief that the material was removed or disabled as a result of mistake or misidentification;', 'your name, address, and telephone number; and', 'a statement that you consent to the jurisdiction of the federal district court for the judicial district in which your address is located (or, if your address is outside the United States, any judicial district in which Maker may be found) and that you will accept service of process from the person who provided the original notice or that person’s agent.']} />
        <p>
          If Maker receives a valid counter-notice, we will forward it to the person who
          submitted the original notice. Unless that person notifies us that they have
          filed a court action seeking to restrain the allegedly infringing activity, we
          may restore the removed material within 10 to 14 business days after receiving
          the counter-notice.
        </p>
        <P num="15.5">
          <span className="font-medium text-slate-800">Misrepresentations.</span><span> Under 17
          U.S.C. § 512(f), any person who knowingly and materially misrepresents that
          material is infringing, or that material was removed or disabled by mistake or
          misidentification, may be liable for damages, including costs and
          attorneys&rsquo; fees.
        </span></P>
        <P num="15.6">
          <span className="font-medium text-slate-800">Designated Agent.</span><span> Send DMCA
          notices and counter-notices to:
        </span></P>
        <Address lines={['DMCA Designated Agent', 'Maker, Inc.', '548 Market St PMB 35672', 'San Francisco, CA 94104-5401', 'Email: copyright@maker.co']} />
      </Section>

      <Section number="16" title="Suspension and Termination">
        <P num="16.1">
          <span className="font-medium text-slate-800">Cancellation by you.</span><span> You may
          stop using the Services at any time. You may cancel a paid plan or close your
          account at any time using the cancellation or account-closure options in the
          Services, where available, or by contacting support@maker.co. Cancelling a
          paid plan stops future renewals; unless</span>{' '}
          <Ref id="11"><span>Section 11 (Fees and Billing (Self-Serve Plans))</span></Ref><span> or your
          Order Form states otherwise, your plan remains active through the end of the
          then-current billing period, and any effect of cancellation on fees already
          paid is as described in Section 11.
        </span></P>
        <P num="16.2">
          <span className="font-medium text-slate-800">Suspension and termination by Maker.</span>{' '}<span>
          Maker may suspend or terminate your account, or your access to some or all of
          the Services, if: (a) you materially breach these Terms, including</span>{' '}
          <Ref id="10"><span>Section 10 (Acceptable Use)</span></Ref><span>; (b) you engage in fraudulent,
          deceptive, or illegal activity, or your use of the Services creates a material
          risk of harm to Maker, other customers, or third parties; (c) you fail to pay
          fees when due; (d) suspension or termination is reasonably necessary to
          address a security risk or protect the integrity or operation of the Services;
          or (e) Maker is required to act by law, legal process, or a governmental or
          law-enforcement request. Where practicable, Maker will give you notice before
          &mdash; or promptly after &mdash; suspending or terminating your account, and
          will describe the reason where the law and the circumstances allow. Maker may
          act without prior notice where necessary to comply with law, respond to a
          security incident, or prevent imminent harm. Where Maker determines that
          suspension is sufficient to address an issue, Maker may suspend access while
          the issue is investigated or resolved and restore access once it is. Maker may
          also terminate: (f) a free account, for any or no reason, on at least thirty
          (30) days&rsquo; notice to you; and (g) any account, if Maker discontinues the
          Services or the material features your plan uses, on at least thirty (30)
          days&rsquo; notice (or as much notice as is reasonably practicable) &mdash; in
          each case with the effect described in Section 16.4 (Effect of termination),
          including the export window in Section 16.4(c) for paid accounts. Closure of a
          free account for inactivity is governed exclusively by Section 16.3 and not by
          clause (f). Suspension or termination exercised in good faith under this
          Section 16 is without liability to you, to the fullest extent permitted by law
          and subject to </span><Ref id="20"><span>Section 20 (Limitation of Liability)</span></Ref><span>; this
          does not limit any express refund or export rights under Section 11 (Fees and
          Billing (Self-Serve Plans)), Section 16.4, or your Order Form.
        </span></P>
        <P num="16.3">
          <span className="font-medium text-slate-800">Dormant free accounts.</span><span> Maker
          may close a free account that has been inactive for an extended period, but
          only after giving notice to the email address associated with the account and
          a reasonable opportunity to keep the account active.
        </span></P>
        <P num="16.4">
          <span className="font-medium text-slate-800">Effect of termination.</span><span> When
          your account is cancelled or terminated: (a) your right to access and use the
          Services ends, and the licenses Maker granted you under these Terms end with
          it; (b) you remain responsible for all amounts incurred before the effective
          date of cancellation or termination; and (c) for paid accounts, Maker will
          make Customer Content associated with the account available for export for at
          least sixty (60) days after cancellation or termination, except where export
          is prohibited by law or legal process or the account was terminated for fraud
          or illegal activity. After that export period (or, for free accounts, after
          closure), Maker may delete Customer Content associated with the account and
          will handle personal data as described in the Maker Privacy Policy (see</span>{' '}
          <Ref id="5"><span>Section 5 (Privacy)</span></Ref><span>). Maker has no obligation to retain
          Customer Content after that period, except as required by applicable law.
          Maker-provided materials (for example, templates) that were incorporated into
          Customer Content during your term may continue to be used as part of that
          exported Customer Content after termination; this does not permit extracting
          those materials for standalone use or using them to create new content.
          Nothing in this Section limits Maker&rsquo;s ability to preserve or disclose
          information as described in Section 5 (Privacy) where legally required.
        </span></P>
        <P num="16.5">
          <span className="font-medium text-slate-800">Survival.</span><span> The following
          survive cancellation, termination, or expiration of these Terms: Section 7
          (Your Content), including Section 7.1 (your ownership) and Section 7.5 (your
          content representations), and, to the extent needed to complete any export
          under Section 16.4 and any deletion, the remainder of Section 7; Section 11
          (Fees and Billing (Self-Serve Plans)), as to amounts accrued but unpaid;
          Section 12 (Maker&rsquo;s Intellectual Property); Section 13 (Feedback);
          Section 16.4 (Effect of termination) and this Section 16.5; Section 19
          (Disclaimer of Warranties); Section 20 (Limitation of Liability); Section 21
          (Indemnification); Section 22 (Export Controls and Sanctions); Section 23
          (Governing Law; Venue; Time to Bring Claims); Section 25 (General Terms);
          Section 26 (Contact and Reporting); and any other provision that by its nature
          should survive.
        </span></P>
      </Section>

      <Section number="17" title="Changes to the Services">
        <p>
          The Services evolve. Maker may add, change, or remove features or
          functionality, and may suspend or discontinue the Services in whole or in
          part, at any time. If a change will materially reduce the core functionality
          of a paid plan, Maker will use commercially reasonable efforts to notify you
          in advance by email or through the Services. Subject to{' '}
          <Ref id="16"><span>Section 16.4 (Effect of termination)</span></Ref> and, for organizations
          with an Enterprise Agreement,{' '}
          <Ref id="2"><span>Section 2 (Enterprise Agreements; Order of Precedence)</span></Ref>, Maker
          will not be liable to you or any third party for modifying, suspending, or
          discontinuing the Services or any part of them.
        </p>
      </Section>

      <Section number="18" title="Beta and Pilot Features">
        <p>
          Maker may make features available that are identified as alpha, beta, pilot,
          preview, early access, or similar (&ldquo;Beta Features&rdquo;). Beta
          Features: (a) are provided &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE,&rdquo;
          without warranties of any kind; (b) may be changed, suspended, or discontinued
          at any time, with or without notice, and may never be made generally
          available; (c) may be subject to separate or additional terms presented when
          you access them, which govern that feature to the extent of any conflict with
          this section; and (d) are excluded from any commitments Maker makes elsewhere
          in these Terms. Sections{' '}
          <Ref id="19"><span>19 (Disclaimer of Warranties)</span></Ref> and{' '}
          <Ref id="20"><span>20 (Limitation of Liability)</span></Ref> apply fully to Beta Features.
        </p>
      </Section>

      <Section number="19" title="Disclaimer of Warranties">
        <p className="uppercase text-sm">
          19.1 The Services, and all content and materials made available through the
          Services, are provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo;
          without warranties of any kind. To the fullest extent permitted by law, Maker
          and its affiliates, licensors, and suppliers disclaim all warranties, express
          or implied, including the implied warranties of merchantability, fitness for a
          particular purpose, title, and non-infringement, and any warranties arising
          out of course of dealing or usage of trade.
        </p>
        <p className="uppercase text-sm">
          19.2 Without limiting Section 19.1, Maker does not warrant that the Services
          will be uninterrupted, timely, secure, or error-free, or that any content or
          materials available through the Services &mdash; including Customer Content of
          other users and AI Output (see Section 8 (AI Features and AI Output)) &mdash;
          will be accurate, complete, reliable, or fit for your purposes. No advice or
          information, whether oral or written, obtained from Maker or through the
          Services creates any warranty not expressly stated in these Terms.
        </p>
        <p className="uppercase text-sm">
          19.3 Some jurisdictions do not allow the exclusion of implied warranties, so
          some of the above exclusions may not apply to you.
        </p>
      </Section>

      <Section number="20" title="Limitation of Liability">
        <p className="uppercase text-sm">
          20.1 To the fullest extent permitted by law, neither Maker nor its affiliates,
          directors, officers, employees, agents, licensors, or suppliers will be liable
          for any indirect, incidental, special, punitive, or consequential damages, or
          for any loss of profits, revenue, goodwill, or data, arising out of or
          relating to these Terms or the Services, under any legal theory (warranty,
          contract, tort including negligence, statute, or otherwise), even if Maker has
          been advised of the possibility of such damages and even if a limited remedy
          fails of its essential purpose.
        </p>
        <p className="uppercase text-sm">
          20.2 To the fullest extent permitted by law, Maker&rsquo;s total aggregate
          liability arising out of or relating to these Terms or the Services will not
          exceed the greater of (a) one hundred U.S. dollars ($100) or (b) the amounts
          you paid to Maker for the Services in the twelve (12) months preceding the
          event giving rise to the claim. Maker is not liable for any failure or delay
          caused by events beyond its reasonable control.
        </p>
        <p className="uppercase text-sm">
          20.3 Maker is not liable for Customer Content made available by other users or
          for the defamatory, offensive, or illegal conduct of any third party, and the
          risk of harm from the foregoing rests with you to the fullest extent permitted
          by law.
        </p>
        <p className="uppercase text-sm">
          20.4 The limitations in this Section 20 are an essential part of the bargain
          between you and Maker and apply to the fullest extent permitted by law in the
          applicable jurisdiction, but they do not limit any liability that cannot be
          limited under applicable law. If you access the Services under an Enterprise
          Agreement, the liability terms of that agreement control to the extent they
          conflict with this Section 20, as described in{' '}
          <Ref id="2"><span>Section 2 (Enterprise Agreements; Order of Precedence)</span></Ref>.
        </p>
      </Section>

      <Section number="21" title="Indemnification">
        <P num="21.1">
          <span className="font-medium text-slate-800">Your indemnity.</span><span> To the
          fullest extent permitted by applicable law, you will defend, indemnify, and
          hold harmless Maker and its affiliates, and their respective directors,
          officers, employees, agents, licensors, and suppliers, from and against any
          third-party claim, demand, action, or proceeding, and any resulting damages,
          liabilities, penalties, costs, and expenses (including reasonable
          attorneys&rsquo; fees), to the extent arising out of or relating to: (a)
          Customer Content, including any claim that Customer Content infringes,
          misappropriates, or violates a third party&rsquo;s rights, except that this
          clause (a) does not cover a claim to the extent it arises solely from AI
          Output as generated by the Services (before any modification, selection,
          arrangement, or combination by you) or from unmodified templates or other
          materials Maker provides; (b) your violation of these Terms or of applicable
          law; (c) your infringement or violation of any third-party right, including
          any intellectual property, publicity, or privacy right; or (d) Customer Sites,
          including their content and operation and the notices, consents, and
          compliance obligations that apply to them.
        </span></P>
        <P num="21.2">
          <span className="font-medium text-slate-800">Procedure.</span><span> We will notify
          you of any claim subject to this Section 21, although a delay in notice does
          not relieve you of your obligations except to the extent the delay materially
          prejudices you. We may, at our own expense, assume the exclusive defense and
          control of any matter subject to indemnification by you, and you agree to
          cooperate with our defense. You will not settle any claim in a way that
          imposes any obligation or liability on Maker, or admits fault on
          Maker&rsquo;s behalf, without our prior written consent.
        </span></P>
        <P num="21.3">
          <span className="font-medium text-slate-800">Survival.</span><span> This Section 21
          survives any termination of these Terms and your use of the Services.
        </span></P>
      </Section>

      <Section number="22" title="Export Controls and Sanctions">
        <p>
          The Services are subject to United States export-control and
          economic-sanctions laws and regulations, including those administered by the
          U.S. Department of Commerce and the U.S. Department of the Treasury&rsquo;s
          Office of Foreign Assets Control. You will comply with all applicable
          export-control and sanctions laws in connection with the Services. You
          represent and warrant that you &mdash; and, if you are accepting these Terms
          for an organization, that organization &mdash; (a) are not listed on any U.S.
          government list of prohibited, denied, or sanctioned parties; (b) are not
          located in, organized under the laws of, or ordinarily resident in any country
          or region subject to comprehensive U.S. sanctions or embargo; and (c) are not
          owned or controlled by anyone described in (a) or (b). You will not access or
          use the Services from any such country or region, will not permit any
          Authorized User or other person to do so, and will not use the Services for
          any purpose prohibited by applicable export-control or sanctions laws.
        </p>
      </Section>

      <Section number="23" title="Governing Law; Venue; Time to Bring Claims">
        <P num="23.1">
          <span className="font-medium text-slate-800">Governing law.</span><span> These Terms,
          and any dispute or claim arising out of or relating to these Terms or the
          Services, are governed by the laws of the State of California, excluding its
          conflict-of-laws rules.
        </span></P>
        <P num="23.2">
          <span className="font-medium text-slate-800">Venue.</span><span> Any action or
          proceeding arising out of or relating to these Terms or the Services must be
          brought exclusively in the state or federal courts located in San Francisco
          County, California. You and Maker each consent to the personal jurisdiction of
          those courts and waive any objection to venue there, including any objection
          that the forum is inconvenient.
        </span></P>
        <P num="23.3">
          <span className="font-medium text-slate-800">Time to bring claims.</span><span> To the
          fullest extent permitted by applicable law, any claim arising out of or
          relating to these Terms or the Services must be filed within one (1) year
          after the date the claim accrues; claims not filed within that period are
          permanently barred.
        </span></P>
      </Section>

      <Section number="24" title="Changes to These Terms">
        <P num="24.1">
          <span className="font-medium text-slate-800">Updates.</span><span> We may update
          these Terms from time to time. The &ldquo;Last Updated&rdquo; date at the top
          of these Terms shows when they were last revised. Non-material changes (for
          example, clarifications, formatting, or corrections) take effect when the
          updated Terms are posted.
        </span></P>
        <P num="24.2">
          <span className="font-medium text-slate-800">Notice of material changes.</span>{' '}<span>
          If we make material changes to these Terms, we will notify you before the
          changes take effect. If you have an account, we will notify you by email to
          the address associated with your account or by notice within the Services; for
          visitors without an account, we will give notice on our websites. The notice
          will designate a reasonable period (the &ldquo;Notice Period&rdquo;) after
          which the updated Terms take effect. Material changes apply prospectively only
          and do not affect any claim or dispute that arose before their effective date.
        </span></P>
        <P num="24.3">
          <span className="font-medium text-slate-800">If you do not agree; acceptance.</span>{' '}<span>
          If you do not agree to the updated Terms, cancel your subscription or stop
          using the Services before the updated Terms take effect. If you continue to
          use the Services after the Notice Period ends, the updated Terms apply to you
          and your use of the Services from that point forward.
        </span></P>
        <P num="24.4">
          <span className="font-medium text-slate-800">Enterprise Agreements unaffected.</span>{' '}<span>
          Updates to these Terms do not amend any Enterprise Agreement, and</span>{' '}
          <Ref id="2"><span>Section 2 (Enterprise Agreements; Order of Precedence)</span></Ref>{' '}<span>
          applies to each version of these Terms.
        </span></P>
      </Section>

      <Section number="25" title="General Terms">
        <P num="25.1">
          <span className="font-medium text-slate-800">Assignment.</span><span> You may not
          assign, transfer, or delegate these Terms, or any rights or obligations under
          them, in whole or in part, without Maker&rsquo;s prior written consent, and any
          attempt to do so without that consent is void. Maker may assign or transfer
          these Terms, in whole or in part, without restriction, including to an
          affiliate or in connection with a merger, acquisition, corporate
          reorganization, or sale of all or substantially all of its assets. These Terms
          bind and benefit the parties and their permitted successors and assigns.
        </span></P>
        <P num="25.2">
          <span className="font-medium text-slate-800">Severability.</span><span> If any
          provision of these Terms is held invalid, illegal, or unenforceable, that
          provision will be limited or eliminated to the minimum extent necessary, and
          the remaining provisions will continue in full force and effect.
        </span></P>
        <P num="25.3">
          <span className="font-medium text-slate-800">No waiver.</span><span> Maker&rsquo;s
          failure to exercise or enforce any right or provision of these Terms is not a
          waiver of that right or provision. A waiver is effective only if in writing,
          and only for the specific instance stated.
        </span></P>
        <P num="25.4">
          <span className="font-medium text-slate-800">No third-party beneficiaries.</span>{' '}<span>
          These Terms are for the benefit of you and Maker only. They do not create any
          right or benefit in any other person, and there are no third-party
          beneficiaries to these Terms &mdash; except that the affiliates, licensors,
          suppliers, directors, officers, employees, and agents referenced in</span>{' '}
          <Ref id="19"><span>Section 19 (Disclaimer of Warranties)</span></Ref><span>,</span>{' '}
          <Ref id="20"><span>Section 20 (Limitation of Liability)</span></Ref><span>, and</span>{' '}
          <Ref id="21"><span>Section 21 (Indemnification)</span></Ref><span> may rely on and enforce those
          Sections.
        </span></P>
        <P num="25.5">
          <span className="font-medium text-slate-800">Relationship of the parties.</span>{' '}<span>
          You and Maker are independent contractors. These Terms do not create a
          partnership, joint venture, agency, franchise, fiduciary, or employment
          relationship, and neither of us has authority to bind the other in any
          respect.
        </span></P>
        <P num="25.6">
          <span className="font-medium text-slate-800">Taxes on your activity.</span><span> You
          are responsible for determining, paying, withholding, filing, and reporting
          all taxes, duties, and other governmental assessments associated with your
          activity in connection with the Services. Taxes on fees you pay to Maker are
          addressed in </span><Ref id="11"><span>Section 11 (Fees and Billing (Self-Serve Plans))</span></Ref><span>.
        </span></P>
        <P num="25.7">
          <span className="font-medium text-slate-800">Services operated from the United States.</span>{' '}<span>
          Maker provides and operates the Services from the United States. Maker makes no
          representation that the Services are appropriate or available for use in any
          other location. If you access or use the Services from outside the United
          States, you do so on your own initiative and are responsible for compliance
          with the laws that apply to you locally.
        </span></P>
        <P num="25.8">
          <span className="font-medium text-slate-800">Notices.</span><span> Maker may give
          notices under these Terms by email to the address associated with your
          account, through the Services, or by posting on maker.co, and those notices
          are deemed given when sent or posted. You are responsible for keeping the
          email address associated with your account accurate and current. Notices to
          Maker must be sent by email to legal@maker.co or by mail to the postal address
          in </span><Ref id="26"><span>Section 26 (Contact and Reporting)</span></Ref><span>. Copyright notices
          must follow </span><Ref id="15"><span>Section 15 (Copyright Policy (DMCA))</span></Ref><span>.
        </span></P>
        <P num="25.9">
          <span className="font-medium text-slate-800">Entire agreement; order of precedence.</span>{' '}<span>
          These Terms, together with any terms expressly incorporated by reference
          (including plan-specific terms presented at checkout and any supplemental
          terms for particular features, such as Beta Features under</span>{' '}
          <Ref id="18"><span>Section 18 (Beta and Pilot Features)</span></Ref><span>), are the entire
          agreement between you and Maker regarding the Services and supersede all prior
          and contemporaneous agreements, communications, and understandings regarding
          the Services &mdash; except as provided in</span>{' '}
          <Ref id="2"><span>Section 2 (Enterprise Agreements; Order of Precedence)</span></Ref><span>.
          Nothing in these Terms supersedes, amends, or limits any Enterprise Agreement;
          if there is a conflict between these Terms and an Enterprise Agreement
          (including any Order Form forming part of it), the Enterprise Agreement
          controls for that organization and its Authorized Users, as described in
          Section 2. A standalone self-serve Order Form governs only the plan, fees, and
          usage allotments it sets and is otherwise subject to these Terms.
        </span></P>
      </Section>

      <Section number="26" title="Contact and Reporting">
        <P num="26.1">
          <span className="font-medium text-slate-800">Reporting violations.</span><span> Please
          report suspected violations of these Terms &mdash; including content or
          behavior that violates </span><Ref id="10"><span>Section 10 (Acceptable Use)</span></Ref><span> &mdash;
          to terms@maker.co. Where possible, describe your concern and include a link
          (URL) to the content or account in question. Maker may investigate reports and
          take any action it considers appropriate, including the actions described in</span>{' '}
          <Ref id="16"><span>Section 16 (Suspension and Termination)</span></Ref><span>, but Maker is not
          obligated to take any particular action or to respond to every report.
        </span></P>
        <P num="26.2">
          <span className="font-medium text-slate-800">Contacting Maker.</span><span> For
          support questions, contact support@maker.co. For legal notices, contact
          legal@maker.co (see </span><Ref id="25"><span>Section 25.8 (Notices)</span></Ref><span>). For copyright
          notices, follow the process in</span>{' '}
          <Ref id="15"><span>Section 15 (Copyright Policy (DMCA))</span></Ref><span>.
        </span></P>
        <P num="26.3">
          <span className="font-medium text-slate-800">Postal address.</span>
        </P>
        <Address lines={['Maker, Inc.', '548 Market St PMB 35672', 'San Francisco, CA 94104-5401']} />
      </Section>
    </LegalDoc>;
}